import React, { useState, useRef } from "react";
import {
  Button,
  Box,
  ClickAwayListener,
  Collapse,
  Paper,
  Typography,
  ButtonGroup,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import strings from "global/constants/StringConstants";

/**
 * `alignItems: stretch` is what lets the caret track the main half's height.
 * It is the flex default, but stated so a caller's `buttonGroupParentSx` (or a
 * future theme override) cannot quietly centre the children and re-introduce
 * the stepped bottom edge.
 */
const buttonGroupBaseSx: SxProps<Theme> = {
  bgcolor: "white",
  alignItems: "stretch",
};

// Dropdown menu chrome, ported from the Claude "Create Request" menu
// (`Dashboard.dc.html`): a soft rounded card whose rows are ink-on-white with a
// lavender hover. Every value maps onto an existing `signer.*` palette token.
const menuPaperSx: SxProps<Theme> = {
  width: 220,
  p: "6px",
  borderRadius: "12px",
  border: (t) => `1px solid ${t.palette.signer.border.light}`,
  boxShadow: (t) => `0 10px 30px ${alpha(t.palette.signer.ink, 0.14)}`,
};

const menuItemSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minHeight: "auto",
  px: "12px",
  py: "11px",
  borderRadius: "9px",
  cursor: "pointer",
  "&:hover": { backgroundColor: (t) => t.palette.signer.surface.muted },
};

const menuIconSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  color: (t) => t.palette.signer.ink,
};

const menuLabelSx: SxProps<Theme> = {
  fontSize: "13.5px",
  fontWeight: 600,
  color: (t) => t.palette.signer.ink,
};

export interface DropdownMenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  endIcon?: React.ReactNode;
  tooltip?: string;
}

interface CustomDropdownButtonProps {
  buttonLabel: string;
  menuItems: DropdownMenuItemProps[];
  startIcon?: React.ReactNode;
  disabled?: boolean;
  /**
   * Disables only the main action, leaving the caret menu usable — for a
   * primary action that's blocked while its alternatives (e.g. Save as Draft)
   * still apply. `disabled` disables both halves.
   */
  mainDisabled?: boolean;
  handleMainClick?: Function;
  sx?: any;
  openUpward?: boolean;
  hasSignAccess?: boolean;
  hasTemplateAccess?: boolean;
  buttonGroupSx?: any;
  arrowButtonSx?: any;
  buttonGroupParentSx?: any;
  isNotShowDropdownButton?: boolean;
}

const CustomDropdownButton: React.FC<CustomDropdownButtonProps> = ({
  buttonLabel,
  menuItems,
  disabled = false,
  mainDisabled = false,
  handleMainClick,
  sx,
  startIcon,
  openUpward = false,
  hasSignAccess,
  hasTemplateAccess,
  buttonGroupSx,
  arrowButtonSx,
  isNotShowDropdownButton = true,
  buttonGroupParentSx,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleDropdownToggle = () => {
    setDropdownOpen((open) => !open);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
  };

  const handleMenuItemClick = (onClick: () => void) => {
    onClick();
    handleDropdownClose();
  };

 const hideMainButton =
  (buttonLabel === strings.send && !hasSignAccess) ||
  (buttonLabel === strings.save && !hasTemplateAccess) ||
  (buttonLabel === strings.update && !hasTemplateAccess);

  // Height, font weight, radius, hover, focus-visible and elevation now come
  // from the global `MuiButton` theme — the two halves only carry what the
  // split layout itself needs. Callers still layer colour (and any bespoke
  // geometry) via `buttonGroupSx` / `arrowButtonSx`, spread last so they win.
  // Both halves render the design's split-button chrome by default, from
  // `palette.splitButton`. Callers that pass nothing (side nav, Dashboard) used
  // to fall through to the flat brand fill and read as a different component
  // from the re-skinned headers; now every instance matches and a caller's
  // `buttonGroupSx` / `arrowButtonSx` is only needed for genuine per-screen
  // size differences. Both are spread last so those still win.
  const mainButtonSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    gap: (t) => t.palette.splitButton.gap,
    minWidth: 0,
    height: "auto",
    minHeight: "unset",
    p: (t) => t.palette.splitButton.padding,
    background: (t) => t.palette.splitButton.bg,
    color: (t) => t.palette.splitButton.color,
    borderRadius: (t) => `${t.palette.splitButton.radius} 0 0 ${t.palette.splitButton.radius}`,
    fontSize: (t) => t.palette.splitButton.fontSize,
    fontWeight: (t) => t.palette.splitButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    // The theme gives every button an 8px `startIcon` margin. Left on, it
    // stacks with the gap above and spaces the icon almost twice as far as the
    // design — `gap` is the single source here.
    "& .MuiButton-startIcon": { m: 0 },
    // ButtonGroup paints its own divider as a border on the main half. The
    // caret carries the real seam via its `borderLeft`, so this one has to
    // disappear — transparent, not white. A white border does not vanish
    // against a coloured fill, it reads as a 1px gap between the two halves.
    borderColor: "transparent !important",
    "&:hover": {
      background: (t) => t.palette.splitButton.hoverBg,
      boxShadow: "none",
    },
    ...buttonGroupSx,
  };

  // Caret half welded to the main one, one shade darker with a hairline seam.
  const caretButtonSx: SxProps<Theme> = {
    width: (t) => t.palette.splitButton.caretWidth,
    minWidth: (t) => t.palette.splitButton.caretWidth,
    flexShrink: 0,
    p: 0,
    background: (t) => t.palette.splitButton.caretBg,
    color: (t) => t.palette.splitButton.color,
    borderRadius: (t) => `0 ${t.palette.splitButton.radius} ${t.palette.splitButton.radius} 0`,
    borderLeft: (t) => `1px solid ${t.palette.splitButton.caretDivider}`,
    boxShadow: "none",
    "& .MuiSvgIcon-root": {
      width: (t: Theme) => t.palette.splitButton.caretIconSize,
      height: (t: Theme) => t.palette.splitButton.caretIconSize,
      fontSize: (t: Theme) => t.palette.splitButton.caretIconSize,
    },
    // Height is owned here, not by the caller. The theme pins every contained
    // button to a fixed height, but a main half styled `height: auto` is sized
    // by its padding instead — that mismatch left the caret several px short
    // and stepped the bottom edge. Stretching to the group tracks whatever the
    // main half resolves to, at any padding or font size.
    alignSelf: "stretch",
    height: "auto",
    minHeight: "unset",
    "&:hover": {
      background: (t) => t.palette.splitButton.caretHoverBg,
      boxShadow: "none",
    },
    ...arrowButtonSx,
  };

  return (
    <Box
      ref={anchorRef}
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {openUpward ? (
        <>
          <ClickAwayListener onClickAway={handleDropdownClose}>
            <div style={{ ...buttonGroupParentSx}}>
              <ButtonGroup
                variant="contained"
                disabled={disabled}
                sx={buttonGroupBaseSx}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    handleMainClick ? handleMainClick() : handleDropdownToggle()
                  }
                  startIcon={startIcon}
                  sx={mainButtonSx}
                  disabled={disabled || mainDisabled}
                >
                  {buttonLabel}
                </Button>
                {menuItems.length > 0 && isNotShowDropdownButton && (
                  <Button
                    onClick={handleDropdownToggle}
                    aria-label={`${dropdownOpen ? "Close" : "Open"} ${buttonLabel} menu`}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                    sx={caretButtonSx}
                  >
                    <KeyboardArrowDownIcon
                      aria-hidden="true"
                      sx={{
                        transform: dropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </ClickAwayListener>
          <Menu
            anchorEl={anchorRef.current}
            open={dropdownOpen}
            onClose={handleDropdownClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            PaperProps={{
              sx: [sx, menuPaperSx, { mt: "-8px" }],
            }}
            MenuListProps={{
              sx: { py: 0 },
            }}
          >
            {menuItems.map((item, idx) => {
              const menuItemNode = (
                <MenuItem
                  key={item.label + idx}
                  disabled={item.disabled}
                  onClick={() => handleMenuItemClick(item.onClick)}
                  sx={menuItemSx}
                >
                  <Box sx={menuIconSx}>{item.icon}</Box>
                  <Typography sx={menuLabelSx}>{item.label}</Typography>
                  {item.endIcon && (
                    <Box
                      sx={{
                        ml: "auto",
                        pl: 1.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item.endIcon}
                    </Box>
                  )}
                </MenuItem>
              );
              return item.tooltip ? (
                <Tooltip
                  key={item.label + idx}
                  title={item.tooltip}
                  placement="left"
                  arrow
                >
                  <Box component="span" sx={{ display: "block" }}>
                    {menuItemNode}
                  </Box>
                </Tooltip>
              ) : (
                menuItemNode
              );
            })}
          </Menu>
        </>
      ) : (
        <ClickAwayListener onClickAway={handleDropdownClose}>
          <div
            style={{ ...buttonGroupParentSx }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && dropdownOpen) {
                event.stopPropagation();
                handleDropdownClose();
              }
            }}
          >
            <ButtonGroup
              variant="contained"
              disabled={disabled}
              sx={buttonGroupBaseSx}
            >
              {!hideMainButton && (
                <Button
                  variant="contained"
                  onClick={() =>
                    handleMainClick ? handleMainClick() : handleDropdownToggle()
                  }
                  startIcon={startIcon}
                  aria-label={
                    menuItems.length > 0 && !handleMainClick
                      ? `${buttonLabel} menu`
                      : buttonLabel
                  }
                  aria-expanded={
                    menuItems.length > 0 && !handleMainClick
                      ? dropdownOpen
                      : undefined
                  }
                  aria-haspopup={
                    menuItems.length > 0 && !handleMainClick
                      ? "true"
                      : undefined
                  }
                  sx={mainButtonSx}
                  disabled={disabled || mainDisabled}
                >
                  {buttonLabel}
                </Button>
              )}
              {menuItems.length > 0 && isNotShowDropdownButton && (
                <Button
                  onClick={handleDropdownToggle}
                  aria-label={`${dropdownOpen ? "Close" : "Open"} ${buttonLabel} menu`}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                  sx={caretButtonSx}
                >
                  <KeyboardArrowDownIcon
                    aria-hidden="true"
                    sx={{
                      transform: dropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </Button>
              )}
            </ButtonGroup>
            <Collapse in={dropdownOpen}>
              <Paper
                elevation={0}
                sx={[
                  sx,
                  menuPaperSx,
                  {
                    position: "absolute",
                    top: "100%",
                    mt: "8px",
                    right: 0,
                    zIndex: 1300,
                    opacity: dropdownOpen ? 1 : 0,
                    visibility: dropdownOpen ? "visible" : "hidden",
                    transition: "opacity 0.2s ease, visibility 0.2s ease",
                  },
                ]}
              >
                {menuItems.map((item, idx) => {
                  const isItemDisabled = !!item.disabled;
                  const rowNode = (
                    <Box
                      role="button"
                      tabIndex={isItemDisabled ? -1 : 0}
                      aria-disabled={isItemDisabled || undefined}
                      onKeyDown={(e) => {
                        if (isItemDisabled) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleMenuItemClick(item.onClick);
                        }
                      }}
                      sx={[
                        menuItemSx,
                        {
                          cursor: isItemDisabled ? "not-allowed" : "pointer",
                          opacity: isItemDisabled ? 0.5 : 1,
                          pointerEvents: isItemDisabled ? "none" : "auto",
                          "&:hover": {
                            backgroundColor: (theme: Theme) =>
                              isItemDisabled
                                ? "transparent"
                                : theme.palette.signer.surface.muted,
                          },
                          "&:focus-visible": {
                            outline: (theme: Theme) =>
                              `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: "-2px",
                          },
                        },
                      ]}
                      onClick={
                        isItemDisabled
                          ? undefined
                          : () => handleMenuItemClick(item.onClick)
                      }
                    >
                      <Box sx={menuIconSx}>{item.icon}</Box>
                      <Typography sx={menuLabelSx}>{item.label}</Typography>
                      {item.endIcon && (
                        <Box
                          sx={{
                            ml: "auto",
                            pl: 1.5,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {item.endIcon}
                        </Box>
                      )}
                    </Box>
                  );
                  return item.tooltip ? (
                    <Tooltip
                      key={item.label + idx}
                      title={item.tooltip}
                      placement="left"
                      arrow
                    >
                      <Box
                        component="span"
                        sx={{ display: "block", pointerEvents: "auto" }}
                      >
                        {rowNode}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box key={item.label + idx}>{rowNode}</Box>
                  );
                })}
              </Paper>
            </Collapse>
          </div>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default CustomDropdownButton;
